import "./TopBar.scss";
import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import UserSettings from "./Modals/UserSettings/UserSettings";
import Modal from "../Modal/Modal";
import ToolTip from "../ToolTip/ToolTip";
import ProjectsModal from "./Modals/ProjectsModal/ProjectsModal";
import PeopleModal from "./Modals/PeopleModal/PeopleModal";
import useAuthStore from "../../store/authStore";
import OrganizationalStructureModal from "./Modals/OrganizationsModal/OrganizationsModal";
import SuperSearchModal from "./Modals/SuperSearchModal/SuperSearchModal";
import NotificationsModal from "./Modals/NotificationsModal/NotificationsModal";
import UserSettingsModal from "./Modals/UserSettingsModal/UserSettingsModal";
import AccountModal from "./Modals/AccountModal/AccountModal";
import AvatarModal from "./Modals/AvatarModal/AvatarModal";
import ReceiptsModal from "./Modals/ReceiptsModal/ReceiptsModal";
import BrandTitle from "../BrandTitle/BrandTitle";
import ChatModal from "../ModalContent/ChatModal/ChatModal";
import useChatStore from "../../store/chatStore";
import { useNotification } from "../../context/NotificationContext";
import { getOrganizationMembers } from "../../api/organizations/organizations";
import OnboardingPointer from "../OnboardingPointer/OnboardingPointer";
import md5 from "md5";

// Page title shown next to the logo, by route
const PAGE_TITLE_KEYS = {
  dashboard: "topBar.pages.dashboard",
  "my-day": "topBar.pages.myDay",
  boards: "topBar.pages.boards",
  notes: "topBar.pages.notes",
  todolists: "topBar.pages.todolists",
  "project-lists": "topBar.pages.projectLists",
  "personal-lists": "topBar.pages.personalLists",
  help: "topBar.pages.help",
  demo: "topBar.pages.demo",
  tasks: "topBar.pages.tasks",
  postits: "topBar.pages.postits",
  superdashboard: "topBar.pages.superdashboard",
};

function TopBar({ onClosePostIts }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState("dashboard");
  const { currentProject, user, organizations, token } = useAuthStore();
  const { getTotalUnreadCount, conversations } = useChatStore();
  const { notificationStatus, unreadCount } = useNotification();
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [showChatButton, setShowChatButton] = useState(false);

  const modalRef = useRef();
  const toolTipRef = useRef();
  const toolTipTimeoutRef = useRef(null);

  // Get current organization
  const currentOrganization = organizations.find(
    (org) => org._id === user?.currentOrganization,
  );

  // Check if user is admin in current organization
  const userId = user?._id || user?.id;
  const isOrgAdmin =
    currentOrganization?.members.find(
      (m) => String(m.userId) === String(userId),
    )?.role === "admin";

  // Check if user needs onboarding
  const hasNoOrganization = user && !currentOrganization;
  const hasOrganizationButNoProjects =
    user && currentOrganization && !currentProject;
  const needsOnboarding = hasNoOrganization || hasOrganizationButNoProjects;

  // Check if user is superAdmin
  const isSuperAdmin = user?.role === "superAdmin";

  // Redirect to help page if no current project exists (except for superAdmin)
  useEffect(() => {
    if (
      user &&
      !currentProject &&
      location.pathname !== "/help" &&
      !isSuperAdmin
    ) {
      navigate("/help");
    }
  }, [user, currentProject, location.pathname, navigate, isSuperAdmin]);

  const showToolTip = (e, arrow, content) => {
    // Clear any pending close timeout
    if (toolTipTimeoutRef.current) {
      clearTimeout(toolTipTimeoutRef.current);
      toolTipTimeoutRef.current = null;
    }

    const triggerElement = e.currentTarget;

    if (toolTipRef.current) {
      toolTipRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const closeToolTip = () => {
    // Use a small delay to allow moving between buttons
    toolTipTimeoutRef.current = setTimeout(() => {
      if (toolTipRef.current) {
        toolTipRef.current.close();
      }
    }, 50);
  };

  const openModal = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const openUserSettingsModal = () => {
    if (modalRef.current) {
      modalRef.current.open({
        arrow: "center-center",
        content: <UserSettingsModal onClose={closeModal} />,
        triggerElement: null,
      });
    }
  };

  const openAccountModal = () => {
    if (modalRef.current) {
      modalRef.current.open({
        arrow: "center-center",
        content: <AccountModal onClose={closeModal} />,
        triggerElement: null,
      });
    }
  };

  const openAvatarModal = () => {
    if (modalRef.current) {
      modalRef.current.open({
        arrow: "center-center",
        content: <AvatarModal onClose={closeModal} />,
        triggerElement: null,
      });
    }
  };

  const openReceiptsModal = () => {
    if (modalRef.current) {
      modalRef.current.open({
        arrow: "center-center",
        content: <ReceiptsModal onClose={closeModal} />,
        triggerElement: null,
      });
    }
  };

  const handleChatClick = (e) => {
    openModal(e, "center-center", <ChatModal onClose={closeModal} />);
  };

  // Check if there are other members in the organization to show chat
  useEffect(() => {
    const checkOrgMembers = async () => {
      // Don't run if we don't have necessary data
      if (
        !token ||
        !user?.currentOrganization ||
        !organizations ||
        organizations.length === 0
      ) {
        setShowChatButton(false);
        return;
      }

      // Check if the currentOrganization actually exists in the organizations array
      const currentOrg = organizations.find(
        (org) => org._id === user.currentOrganization,
      );

      // If organization doesn't exist in the array, don't try to fetch members
      if (!currentOrg) {
        setShowChatButton(false);
        return;
      }

      try {
        const members = await getOrganizationMembers(
          user.currentOrganization,
          token,
        );
        // Show chat button only if there are other active members (not including self and pending)
        const activeMembers = (members || []).filter(
          (member) =>
            member.id !== user.id &&
            member.id !== user._id &&
            member.status !== "pending",
        );
        setShowChatButton(activeMembers.length > 0);
      } catch (error) {
        // Silently fail - this is not critical functionality
        // Organization might not exist or user might not be a member anymore
        setShowChatButton(false);
      }
    };

    checkOrgMembers();
  }, [token, user, organizations]);

  // Update chat unread count when conversations change
  useEffect(() => {
    if (!showChatButton) return;
    const count = getTotalUnreadCount();
    setChatUnreadCount(count);
  }, [showChatButton, conversations, getTotalUnreadCount]);

  useEffect(() => {
    const currentPath = location.pathname.replace("/", "");
    setActivePage(currentPath || "tasks");
  }, [location]);

  // Global keyboard shortcut for Super Search (Cmd/Ctrl + F)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd+F (Mac) or Ctrl+F (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        e.preventDefault(); // Prevent browser's default find dialog

        // Open the super search modal
        if (modalRef.current) {
          modalRef.current.open({
            arrow: "center-center",
            content: <SuperSearchModal onClose={closeModal} />,
            triggerElement: null,
          });
        }
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const capitalizeWords = (str) => {
    const titleKey = PAGE_TITLE_KEYS[str.toLowerCase()];
    if (titleKey) {
      return t(titleKey);
    }

    return str
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="top-bar" onClick={onClosePostIts}>
      <Modal ref={modalRef}></Modal>
      <ToolTip ref={toolTipRef} manualControl={true}></ToolTip>

      {/* Show organization pointer if user has no organization */}
      <OnboardingPointer
        targetSelector=".organizations-button"
        message={t("topBar.onboardingOrganization")}
        show={hasNoOrganization && location.pathname === "/help"}
      />

      {/* Show project pointer if user has organization but no projects */}
      <OnboardingPointer
        targetSelector=".projects-button"
        message={t("topBar.onboardingProject")}
        show={hasOrganizationButNoProjects && location.pathname === "/help"}
      />

      <div className="top-bar-left-wrapper">
        <div className="top-bar-icon">
          <img
            src="/somigo_logo.svg"
            alt="SOMIGO Icon"
            width="45"
            height="45"
          />
        </div>
        <div className="top-bar-title">
          <BrandTitle fontSize={25} />
          <span style={{ marginLeft: "8px" }}>
            - {capitalizeWords(activePage)}
          </span>
        </div>
      </div>
      <div className="top-bar-center"></div>
      <div className="top-bar-right-wrapper">
        {/* <div className="top-bar-right-projects">
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "24px" }}
          >
            notifications
          </span>
        </div> */}
        <div className="top-bar-project-title-wrapper">
          <div
            className="top-bar-organization-title"
            onClick={(e) =>
              openModal(
                e,
                "center-center",
                <OrganizationalStructureModal onClose={closeModal} />,
              )
            }
            style={{ cursor: "pointer" }}
          >
            <>
              {t("topBar.organizationLabel")}&nbsp;&nbsp;
              {currentOrganization ? (
                <span style={{ color: currentOrganization?.color }}>
                  {currentOrganization?.name}
                </span>
              ) : (
                <span>{t("topBar.createOrganization")}</span>
              )}
            </>
          </div>
          <div
            className="top-bar-project-title"
            onClick={(e) =>
              openModal(
                e,
                "center-center",
                <ProjectsModal onClose={closeModal} />,
              )
            }
            style={{ cursor: "pointer" }}
          >
            <>
              {t("topBar.projectLabel")}&nbsp;&nbsp;
              {currentProject?.projectName ? (
                <span style={{ color: currentProject?.color }}>
                  {currentProject?.projectName}
                </span>
              ) : (
                <span>{t("topBar.createProject")}</span>
              )}
            </>
          </div>
        </div>
        <div className="top-bar-spacer"></div>
        <div
          className="top-bar-right-projects organizations-button"
          onMouseEnter={(e) => {
            if (!needsOnboarding) {
              showToolTip(e, "top-right", t("organizations.title"));
            }
          }}
          onMouseLeave={closeToolTip}
          onClick={(e) =>
            openModal(
              e,
              "center-center",
              <OrganizationalStructureModal onClose={closeModal} />,
            )
          }
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "24px" }}
          >
            domain
          </span>
        </div>
        <div
          className="top-bar-right-projects projects-button"
          onMouseEnter={(e) => {
            if (!needsOnboarding) {
              showToolTip(e, "top-right", t("topBar.projects"));
            }
          }}
          onMouseLeave={closeToolTip}
          onClick={(e) =>
            openModal(
              e,
              "center-center",
              <ProjectsModal onClose={closeModal} />,
            )
          }
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "24px" }}
          >
            folder_copy
          </span>
        </div>
        {isOrgAdmin && (
          <div
            className="top-bar-right-projects"
            onMouseEnter={(e) =>
              showToolTip(e, "top-right", t("topBar.peopleAndTeams"))
            }
            onMouseLeave={closeToolTip}
            onClick={(e) =>
              openModal(
                e,
                "center-center",
                <PeopleModal onClose={closeModal} />,
              )
            }
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "24px" }}
            >
              group
            </span>
          </div>
        )}

        <div className="top-bar-spacer"></div>

        <div
          className="top-bar-right-projects notification-button-container"
          onMouseEnter={(e) =>
            showToolTip(e, "top-right", t("notifications.title"))
          }
          onMouseLeave={closeToolTip}
          onClick={(e) =>
            openModal(
              e,
              "top-right",
              <NotificationsModal onClose={closeModal} />,
            )
          }
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "24px" }}
          >
            notifications
          </span>
          {unreadCount > 0 ? (
            <span className="notification-count">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          ) : (
            notificationStatus && (
              <span
                className={`notification-dot notification-dot--${notificationStatus}`}
              ></span>
            )
          )}
        </div>

        <div className="top-bar-spacer"></div>

        <div
          className="top-bar-right-projects"
          onMouseEnter={(e) =>
            showToolTip(e, "top-right", t("topBar.superSearch"))
          }
          onMouseLeave={closeToolTip}
          onClick={(e) =>
            openModal(
              e,
              "center-center",
              <SuperSearchModal onClose={closeModal} />,
            )
          }
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "24px" }}
          >
            saved_search
          </span>
        </div>

        {showChatButton && (
          <div
            className="top-bar-right-projects chat-button-container"
            onMouseEnter={(e) => showToolTip(e, "top-right", t("topBar.chat"))}
            onMouseLeave={closeToolTip}
            onClick={handleChatClick}
          >
            {chatUnreadCount > 0 && (
              <span className="chat-unread-indicator"></span>
            )}
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "24px" }}
            >
              chat
            </span>
          </div>
        )}

        {/* <div
          className="top-bar-right-projects"
          onMouseEnter={(e) =>
            showToolTip(e, "top-right", "Organizational Structure")
          }
          onMouseLeave={closeToolTip}
          onClick={(e) =>
            openModal(
              e,
              "center-center",
              <OrganizationalStructureModal onClose={closeModal} />
            )
          }
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "24px" }}
          >
            graph_2
          </span>
        </div> */}

        <div className="top-bar-spacer"></div>
        <div
          className="top-bar-right-user"
          onMouseEnter={(e) =>
            showToolTip(
              e,
              "top-right",
              <>
                {user?.firstName} {user?.lastName}
                <br />
                {user?.email}
              </>,
            )
          }
          onMouseLeave={closeToolTip}
          onClick={(e) =>
            openModal(
              e,
              "top-right",
              <UserSettings
                onClose={closeModal}
                onOpenSettings={openUserSettingsModal}
                onOpenAccount={openAccountModal}
                onOpenReceipts={openReceiptsModal}
                onOpenAvatar={openAvatarModal}
              />,
            )
          }
        >
          <div
            className="user-avatar"
            style={{
              backgroundColor: user?.profile?.color || "#2196f3",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              fontWeight: "600",
              color: "white",
              cursor: "pointer",
              textShadow: "0 1px 3px rgba(var(--shadow-rgb), 0.75)",
              overflow: "hidden",
            }}
          >
            {user?.profile?.useGravatar && user?.email ? (
              <img
                src={`https://www.gravatar.com/avatar/${md5(user.email.toLowerCase().trim())}?s=200&d=mp`}
                alt={t("topBar.userAvatar")}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  // If image fails to load, hide it and show initials
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <>
                {user?.firstName?.charAt(0).toUpperCase()}
                {user?.lastName?.charAt(0).toUpperCase()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TopBar;
